import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

// Layout
import Layout from './components/layout/Layout';
import { ToastProvider } from './components/ui/Toast';
import { AuthProvider } from './hooks';
import PersistenceManager from './components/PersistenceManager';
import RegistrationFlowManager from './components/RegistrationFlowManager';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

// Pages (lazy loaded)
const Pipeline = React.lazy(() => import('./pages/Pipeline'));
const Vendeurs = React.lazy(() => import('./pages/Vendeurs'));
const Produits = React.lazy(() => import('./pages/Produits'));
const Methodes = React.lazy(() => import('./pages/Methodes'));
const Profil = React.lazy(() => import('./pages/Profil'));
const Parametres = React.lazy(() => import('./pages/Parametres'));
const Factures = React.lazy(() => import('./pages/Factures'));
const Historique = React.lazy(() => import('./pages/Historique'));
const HistoriqueWins = React.lazy(() => import('./pages/HistoriqueWins'));
const Admin = React.lazy(() => import('./pages/Admin'));
const Landing = React.lazy(() => import('./pages/Landing'));
const Auth = React.lazy(() => import('./pages').then(m => ({ default: m.Auth })));
const Legal = React.lazy(() => import('./pages/Legal'));
const CGV = React.lazy(() => import('./pages/CGV'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Billing = React.lazy(() => import('./pages/Billing'));
const BillingSuccess = React.lazy(() => import('./pages/BillingSuccess'));
const BillingCancel = React.lazy(() => import('./pages/BillingCancel'));
const Subscribe = React.lazy(() => import('./pages/Subscribe'));
const VerifyEmailWaiting = React.lazy(() => import('./pages/VerifyEmailWaiting'));
const PlanSelection = React.lazy(() => import('./pages/PlanSelection'));
const Payment = React.lazy(() => import('./pages/Payment'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));

// Error Fallback Component
function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">Une erreur s'est produite</h3>
          </div>
        </div>
        <div className="mt-2">
          <p className="text-sm text-gray-500 mb-4">
            {error.message || 'Une erreur inattendue s\'est produite'}
          </p>
          <button
            onClick={resetErrorBoundary}
            className="btn btn-primary"
          >
            Réessayer
          </button>
        </div>
      </div>
    </div>
  );
}

// Loading Component
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="loading-spinner mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement...</p>
      </div>
    </div>
  );
}
function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ToastProvider>
        <AuthProvider>
          <Router>
          <RegistrationFlowManager />
          <Layout>
            <PersistenceManager />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Routes publiques */}
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/verify-email-waiting" element={<VerifyEmailWaiting />} />
                <Route path="/verify-email-waiting" element={<VerifyEmailWaiting />} />
                <Route path="/auth/plan-selection" element={<PlanSelection />} />
                <Route path="/auth/payment" element={<Payment />} />
                <Route path="/auth/payment-success" element={<PaymentSuccess />} />
                <Route path="/subscribe" element={<Subscribe />} />
                
                {/* Pages légales */}
                <Route path="/legal" element={<Legal />} />
                <Route path="/cgv" element={<CGV />} />
                <Route path="/privacy" element={<Privacy />} />
                
                {/* Routes de facturation */}
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/billing/cancel" element={<BillingCancel />} />
                
                {/* Routes protégées */}
                <Route path="/pipeline" element={
                  <ProtectedRoute>
                    <Pipeline />
                  </ProtectedRoute>
                } />
                <Route path="/vendeurs" element={
                  <ProtectedRoute>
                    <Vendeurs />
                  </ProtectedRoute>
                } />
                <Route path="/produits" element={
                  <ProtectedRoute>
                    <Produits />
                  </ProtectedRoute>
                } />
                <Route path="/methodes" element={
                  <ProtectedRoute>
                    <Methodes />
                  </ProtectedRoute>
                } />
                <Route path="/profil" element={
                  <ProtectedRoute>
                    <Profil />
                  </ProtectedRoute>
                } />
                <Route path="/parametres" element={
                  <ProtectedRoute>
                    <Parametres />
                  </ProtectedRoute>
                } />
                <Route path="/factures" element={
                  <ProtectedRoute>
                    <Factures />
                  </ProtectedRoute>
                } />
                <Route path="/historique" element={
                  <ProtectedRoute>
                    <Historique />
                  </ProtectedRoute>
                } />
                <Route path="/historique-wins" element={
                  <ProtectedRoute>
                    <HistoriqueWins />
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                } />
                <Route path="/billing" element={
                  <ProtectedRoute>
                    <Billing />
                  </ProtectedRoute>
                } />
                
                <Route path="/dashboard" element={<Navigate to="/pipeline" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Layout>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
